import { Injectable } from '@angular/core';
import {
  CostItemService,
  ItemGroupService,
  ProjectManagerService,
  CommunicationManagerService,
  ItemGroup,
  CostItem,
  InvoiceData,
  InvoicePostRequest,
  Versioning,
  BankAccount,
} from '@ops/shared-types';
import { Observable } from 'rxjs';

@Injectable()
export class ProjectExpensesService {
  constructor(
    private costItemService: CostItemService,
    private itemGroupService: ItemGroupService,
    private projectManagerService: ProjectManagerService,
    private communicationManagerService: CommunicationManagerService
  ) {}

  getOrCreateProjectCost(projectId: string, companyId: string) {
    return this.projectManagerService.getOrCreateProjectCost(
      projectId,
      companyId
    ) as Observable<string>;
  }

  getItemGroups(projectId: string) {
    return this.itemGroupService.getAllProjectItemGroups(
      projectId
    ) as Observable<ItemGroup[]>;
  }

  getCostItems(projectId: string) {
    return this.costItemService.getCostItemsByProject(projectId) as Observable<
      CostItem[]
    >;
  }

  getInvoices(versionLink: string) {
    return this.communicationManagerService.get(
      `invoice/all/${versionLink}`
    ) as Observable<InvoiceData[]>;
  }

  getArchivedInvoices(versionLink: string) {
    return this.communicationManagerService.get(
      `invoice/archived/${versionLink}`
    ) as Observable<InvoiceData[]>;
  }

  archiveInvoice(invoice: InvoiceData) {
    return this.communicationManagerService.put(
      `invoice/${invoice._id}`,
      {
        isArchived: true,
      } as InvoiceData,
      [{ headerName: 'projectId', headerValue: invoice.project }]
    ) as Observable<InvoiceData>;
  }

  updateInvoice(invoice: InvoiceData) {
    invoice.status =
      invoice.receivedAmount === 0
        ? 'Sent'
        : invoice.receivedAmount < invoice.invoiceAmount
        ? 'Partial'
        : invoice.receivedAmount === invoice.invoiceAmount
        ? 'Paid'
        : 'Overpaid';

    return this.communicationManagerService.put(
      `invoice/${invoice._id}`,
      {
        paymentExpectedDate: invoice.paymentExpectedDate,
        payments: invoice.payments,
        status: invoice.status,
      } as InvoiceData,
      [{ headerName: 'projectId', headerValue: invoice.project }]
    ) as Observable<InvoiceData>;
  }

  createInvoice(projectId: string, invoice: InvoicePostRequest) {
    return this.communicationManagerService.postAndGetJson(`invoice`, invoice, [
      { headerName: 'projectId', headerValue: projectId },
    ]) as Observable<InvoiceData>;
  }

  deleteInvoice(projectId: string, invoiceId: string) {
    return this.communicationManagerService.delete(`invoice/${invoiceId}`, [
      { headerName: 'projectId', headerValue: projectId },
    ]);
  }

  getVersioning(projectId: string) {
    return this.communicationManagerService.get('project/versioning', [
      { headerName: 'projectId', headerValue: projectId },
    ]) as Observable<Versioning>;
  }

  getBankAccounts() {
    return this.communicationManagerService.get('bankAccount') as Observable<
      BankAccount[]
    >;
  }
}
